import React from 'react';
import { motion } from 'framer-motion';
import { FaCheck, FaQuoteLeft } from 'react-icons/fa';
import Seo from '@/components/seo/Seo';
import { content } from '@/content/sr';
import { breadcrumbJsonLd } from '@/config/seo';

const AboutPage = () => {
  const pageTitle = 'O nama — uvoz i prodaja građevinskog materijala u Užicu';
  const pageDescription = `${content.about.subtitle} ${content.about.description}`;

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDescription}
        path="/o-nama"
        jsonLd={breadcrumbJsonLd([{ name: 'O nama', path: '/o-nama' }])}
      />

      <div data-testid="about-page">
        {/* Hero Section */}
        <section className="relative py-32 md:py-40 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
              alt="O nama"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-brand-primary/80" />
          </div>
          
          <div className="container-custom relative z-10 text-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="caption text-brand-accent block mb-4"
            >
              {content.about.caption}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-5xl md:text-7xl font-light text-white mb-4"
            >
              {content.about.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-neutral-200 text-lg"
            >
              {content.about.subtitle}
            </motion.p>
          </div>
        </section>

        {/* Content Section */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="relative"
              >
                <img
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Wall Lux Trade enterijer"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-brand-accent flex items-center justify-center">
                  <div className="text-center text-white">
                    <span className="block text-4xl font-heading font-semibold">2026.</span>
                    <span className="text-sm uppercase tracking-widest">Godina Osnivanja</span>
                  </div>
                </div>
              </motion.div>

              {/* Text */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <h2 className="font-heading text-3xl md:text-4xl font-light text-brand-primary mb-6">
                  Naša Priča
                </h2>
                <div className="prose prose-lg text-neutral-700 mb-8 space-y-4">
                  {content.about.description.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph.trim()}</p>
                  ))}
                </div>

                {/* Values */}
                <div className="space-y-4">
                  {content.about.values.map((value, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-sm bg-brand-accent flex items-center justify-center flex-shrink-0">
                        <FaCheck className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium text-brand-primary">{value.title}</h4>
                        <p className="text-neutral-500 text-sm">{value.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Reference / utisci klijenata */}
        <section className="section-padding bg-brand-bgSoft">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-2xl mx-auto mb-14 md:mb-16"
            >
              <span className="caption text-brand-accent block mb-3">{content.references.caption}</span>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-brand-primary mb-4">
                {content.references.title}
              </h2>
              <p className="text-neutral-600">{content.references.subtitle}</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {content.references.items.map((item, index) => (
                <motion.article
                  key={item.name}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: index * 0.08 }}
                  className="bg-white p-8 border border-neutral-200/80 shadow-sm hover:shadow-md hover:border-brand-accent/20 transition-all duration-300"
                  data-testid={`about-reference-${index}`}
                >
                  <FaQuoteLeft className="text-brand-accent/40 w-8 h-8 mb-4" aria-hidden />
                  <p className="text-neutral-700 text-sm leading-relaxed mb-6">{item.text}</p>
                  <div className="pt-4 border-t border-neutral-100">
                    <p className="font-medium text-brand-primary">{item.name}</p>
                    <p className="text-neutral-500 text-sm mt-1">{item.company}</p>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;
